import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { GeneratedNodeAttribute, GuestAttributeAccessRequest, GuestGrantView } from "@keydials/api-client";
import { HELP_ANCHORS, INSTANCE_ROLE_NAMES, instanceRoleDescription, isPlausibleEmail } from "@keydials/ui";
import { hexA } from "../../lib/ui";
import { Icon } from "../Icon";
import { api, getAccessToken } from "../../auth/AuthContext";
import { guestInviteOutcome } from "./guestInvite";
import {
  describeScopes, emptySelection, hasAnyScope, leafKey, minimiseScopes, rowRole, sectionLeaves, segmentLeaves,
  setRowRole, setWhole, setWholeRole, toggleLeaves, type GuestSection, type GuestSelection,
} from "./guestScopes";
import { guestInviteFailureMessage } from "../../settings/grantFeedback";
import { ShareRefusalNote } from "../../settings/ShareRefusalNote";

/**
 * The Guests section of the dial Settings page (KEY-297): invite someone outside the organisation to this one
 * dial, and see or revoke the invites already made. The invite reads as a sentence — who, as what, on
 * which part, for how long — and produces a one-time link whose only power is to trigger a code email to
 * the invited inbox, which is what makes handing the link around safe.
 *
 * Sending that link is the server's job since KEY-536 — it used to be nobody's, which is what the bug
 * report was about. The link still appears here, because the send can fail and because the server keeps
 * only its hash: this is the one moment it can be copied. See `guestInvite.ts` for what we say in each case.
 */

type MemberSection = GuestSection;

const GUEST_ROLES = INSTANCE_ROLE_NAMES.filter((r) => r !== "Admin");
const EXPIRY_CHOICES = [1, 3, 7, 14, 30];

const attributeAccessKey = (a: GeneratedNodeAttribute) => `${a.target}:${a.key}`;

const fieldStyle = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 14,
  outline: "none",
} as const;

const labelStyle = { display: "flex", flexDirection: "column", gap: 5, minWidth: 0 } as const;
const labelTextStyle = { fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)" } as const;

function statusText(g: GuestGrantView, now: Date): { text: string; tone: "ok" | "off" } {
  if (g.revokedAtUtc) return { text: "Revoked", tone: "off" };
  const expires = new Date(g.expiresAtUtc);
  const msLeft = expires.getTime() - now.getTime();
  if (Number.isNaN(msLeft) || msLeft <= 0) return { text: "Expired", tone: "off" };
  const days = Math.ceil(msLeft / 86_400_000);
  return { text: days === 1 ? "Expires today" : `Expires in ${days} days`, tone: "ok" };
}

export function GuestsPanel({
  instanceId,
  sections,
  attributes,
  refusal = null,
}: {
  instanceId: string;
  sections: MemberSection[];
  attributes: GeneratedNodeAttribute[];
  /**
   * Why this dial's organisation won't let a guest be invited (KEY-556), or null. Replaces the invite form
   * only — the list stays, because revoking a guest is never refused and is exactly what a closed channel
   * leaves an admin wanting to do.
   */
  refusal?: string | null;
}) {
  const [guests, setGuests] = useState<GuestGrantView[] | null>(null);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("Reader");
  // KEY-852: which parts of the dial the guest gets — the whole dial, or any mix of sections, action points and
  // (where the dial offers it) single items, each with its own role.
  const [selection, setSelection] = useState<GuestSelection>(() => setWhole(true, emptySelection("Reader")));
  const [expiresInDays, setExpiresInDays] = useState(7);
  // KEY-488: every field starts Hidden — a guest sees nothing until it's explicitly switched on here.
  const [fieldAccess, setFieldAccess] = useState<Map<string, "See" | "Edit">>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The freshly created link — shown exactly once, because the server keeps only its hash.
  const [createdLink, setCreatedLink] = useState<{ email: string; url: string; emailSent: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      setGuests(await api.listGuests(token, instanceId));
    } catch {
      setGuests([]);
    }
  }, [instanceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function invite() {
    const token = getAccessToken();
    if (!token || !isPlausibleEmail(email) || !hasAnyScope(sections, selection)) return;
    setBusy(true);
    setError(null);
    setCreatedLink(null);
    setCopied(false);
    try {
      const attributeAccess: GuestAttributeAccessRequest[] = attributes
        .filter((a) => fieldAccess.has(attributeAccessKey(a)))
        .map((a) => ({ target: a.target, attributeKey: a.key, access: fieldAccess.get(attributeAccessKey(a))! }));
      const result = await api.inviteGuest(token, instanceId, {
        email: email.trim(),
        displayName: displayName.trim() || null,
        role: selection.whole ? selection.wholeRole : role,
        scopes: minimiseScopes(sections, selection),
        expiresInDays,
        attributeAccess: attributeAccess.length > 0 ? attributeAccess : null,
      });
      setCreatedLink({
        email: result.grant.email,
        url: `${window.location.origin}/guest/${result.linkToken}`,
        emailSent: result.emailSent,
      });
      setEmail("");
      setDisplayName("");
      setSelection(setWhole(true, emptySelection(role)));
      setFieldAccess(new Map());
      await reload();
    } catch (e) {
      // The server's reason where it gave one: a closed guest channel used to read as a bad email address.
      setError(guestInviteFailureMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink.url);
      setCopied(true);
    } catch {
      // The link stays on screen to copy by hand.
    }
  }

  async function revoke(g: GuestGrantView) {
    const token = getAccessToken();
    if (!token) return;
    try {
      await api.revokeGuest(token, instanceId, g.id);
      await reload();
    } catch {
      setError("Couldn't revoke that access.");
    }
  }

  const now = new Date();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {refusal ? (
        <>
          <ShareRefusalNote text={refusal} />
          {error && <p style={{ margin: 0, fontSize: 12.5, color: "var(--st-overdue)" }}>{error}</p>}
        </>
      ) : (
        <>
          <p style={{ margin: "0 0 4px", fontSize: 12.5, color: "var(--text-faint)" }}>
            Invite someone outside your organisation to this dial only, for a limited time. We email them the
            link, and they prove it&rsquo;s them with a code sent to the same address — so the link on its own
            opens nothing.
          </p>

          <label style={labelStyle}>
            <span style={labelTextStyle}>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.org"
              style={fieldStyle}
              aria-label="Guest email"
              data-help-anchor={HELP_ANCHORS.guestEmail}
            />
          </label>
          <label style={labelStyle}>
            <span style={labelTextStyle}>Name (shown on the dial)</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Optional"
              style={fieldStyle}
              aria-label="Guest name"
            />
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <label style={{ ...labelStyle, flex: 1 }}>
              <span style={labelTextStyle}>Role</span>
              <select
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                  setSelection((sel) => setWholeRole(e.target.value, sel));
                }}
                style={fieldStyle}
                aria-label="Guest role"
              >
                {GUEST_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
                {instanceRoleDescription(role)} New ticks below start with this role; change any row&rsquo;s role in the table.
              </span>
            </label>
            <label style={{ ...labelStyle, width: 110, flex: "none" }}>
              <span style={labelTextStyle}>Access for</span>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                style={fieldStyle}
                aria-label="Guest access duration"
              >
                {EXPIRY_CHOICES.map((d) => <option key={d} value={d}>{d === 1 ? "1 day" : `${d} days`}</option>)}
              </select>
            </label>
          </div>
          <GrantMatrix sections={sections} selection={selection} role={role} onChange={setSelection} />
          {attributes.length > 0 && (
            <div style={{ marginTop: 4, padding: "10px 12px", border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--surface-2)" }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>Fields this guest can see</div>
              <p style={{ margin: "0 0 8px", fontSize: 11.5, color: "var(--text-faint)" }}>
                Every field starts hidden from a guest. Switch on the ones they need.
              </p>
              {attributes.map((a) => {
                const key = attributeAccessKey(a);
                const current = fieldAccess.get(key) ?? null;
                return (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12.5, color: "var(--text)", minWidth: 100, flex: "1 1 100px" }}>
                      {a.label}
                      <span style={{ marginLeft: 6, fontSize: 10.5, color: "var(--text-faint)" }}>{a.target}</span>
                    </span>
                    <div style={{ display: "flex", gap: 6 }}>
                      {([
                        { value: null, label: "Hidden" },
                        { value: "See", label: "See" },
                        { value: "Edit", label: "Edit" },
                      ] as const).map((choice) => {
                        const on = current === choice.value;
                        return (
                          <button
                            key={choice.label}
                            type="button"
                            aria-pressed={on}
                            onClick={() => setFieldAccess((prev) => {
                              const next = new Map(prev);
                              if (choice.value === null) next.delete(key);
                              else next.set(key, choice.value);
                              return next;
                            })}
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              padding: "3px 9px",
                              borderRadius: 99,
                              cursor: "pointer",
                              border: `var(--border-w) solid ${on ? "var(--accent)" : "var(--border)"}`,
                              background: on ? hexA("#2563eb", 0.14) : "var(--surface)",
                              color: on ? "var(--text)" : "var(--text-faint)",
                            }}
                          >
                            {choice.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              className="kd-btn-primary"
              onClick={() => void invite()}
              disabled={!isPlausibleEmail(email) || busy || !hasAnyScope(sections, selection)}
              data-help-anchor={HELP_ANCHORS.guestInvite}
            >
              <Icon name="send" size={16} />
              Send invite
            </button>
          </div>
          {error && <p style={{ margin: 0, fontSize: 12.5, color: "var(--st-overdue)" }}>{error}</p>}

          {createdLink && (() => {
            // The link is shown either way: the server keeps only its hash, so this is the one chance to copy
            // it — and if the email didn't go, copying it is the only way the guest ever gets in.
            const outcome = guestInviteOutcome(createdLink);
            const failed = outcome.tone === "warn";
            return (
              <div
                style={{
                  border: `var(--border-w) solid ${failed ? "color-mix(in srgb, var(--st-risk) 55%, var(--border))" : "var(--accent-border)"}`,
                  background: failed ? "color-mix(in srgb, var(--st-risk) 10%, transparent)" : "var(--accent-soft)",
                  borderRadius: "var(--radius-sm)",
                  padding: "12px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", display: "flex", alignItems: "center", gap: 7 }}>
                  <Icon name={failed ? "flag" : "send"} size={15} />
                  {outcome.headline}
                </div>
                <code
                  style={{
                    fontSize: 12,
                    wordBreak: "break-all",
                    color: "var(--text-muted)",
                    background: "var(--surface-2)",
                    borderRadius: "var(--radius-sm)",
                    padding: "8px 10px",
                  }}
                >
                  {createdLink.url}
                </code>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <button className={failed ? "kd-btn-primary" : "kd-btn"} onClick={() => void copyLink()}>
                    {copied ? "Copied" : "Copy link"}
                  </button>
                  <span style={{ fontSize: 12, color: "var(--text-faint)" }}>{outcome.note}</span>
                </div>
              </div>
            );
          })()}
        </>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
        {guests !== null && guests.length === 0 && (
          <p style={{ color: "var(--text-faint)", fontSize: 14, margin: "4px 0" }}>No guests yet.</p>
        )}
        {guests?.map((g) => {
          const status = statusText(g, now);
          const active = status.tone === "ok";
          return (
            <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 6px", opacity: active ? 1 : 0.6 }}>
              <span
                style={{
                  width: 34, height: 34, borderRadius: 9, flex: "0 0 auto",
                  background: "var(--surface-inset)", color: "var(--text-muted)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Icon name="users" size={16} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)", display: "flex", alignItems: "center", gap: 8 }}>
                  {g.displayName || g.email}
                  <span
                    style={{
                      fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
                      padding: "2px 7px", borderRadius: 99,
                      background: "var(--surface-inset)", color: "var(--text-muted)",
                    }}
                  >
                    Guest
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
                  {g.email} · {describeScopes(g.scopes, sections)}
                </div>
                <div style={{ fontSize: 12, color: active ? "var(--text-faint)" : "var(--text-muted)" }}>
                  {status.text}
                  {g.lastActiveAtUtc
                    ? ` · last active ${new Date(g.lastActiveAtUtc).toLocaleDateString(undefined, { day: "numeric", month: "short" })}${g.lastIp ? ` from ${g.lastIp}` : ""}`
                    : " · not visited yet"}
                </div>
              </div>
              {active && (
                <button
                  onClick={() => void revoke(g)}
                  title="Revoke this guest's access now"
                  aria-label={`Revoke guest access for ${g.displayName || g.email}`}
                  style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-faint)", display: "flex", padding: 4 }}
                >
                  <Icon name="trash" size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The grant matrix (KEY-852): a "Whole dial" row, then each section with its action points beneath it — and, where
 * the dial offers single items, each action point's items beneath that — with the role the guest holds on every
 * row. Real checkboxes, so the keyboard and screen readers get them for free; a row with only some of its rows
 * ticked is indeterminate, which has to be set on the element. A row's role sets every ticked row beneath it;
 * "Mixed" shows when they differ. New ticks take `role`, the invite's default.
 */
function GrantMatrix({
  sections,
  selection,
  role,
  onChange,
}: {
  sections: MemberSection[];
  selection: GuestSelection;
  role: string;
  onChange: (next: GuestSelection) => void;
}) {
  const cell = { padding: "5px 10px", fontSize: 13, color: "var(--text)", textAlign: "left" } as const;
  const roleCell = { ...cell, width: 120 } as const;
  const off = selection.whole;
  return (
    <table
      aria-label="Parts of the dial this guest can reach"
      style={{ width: "100%", borderCollapse: "collapse", border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--surface-2)" }}
    >
      <thead>
        <tr>
          <th scope="col" style={{ ...cell, fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)" }}>Access to</th>
          <th scope="col" style={{ ...roleCell, fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)" }}>Role</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={cell}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600 }}>
              <input type="checkbox" checked={selection.whole} onChange={(e) => onChange(setWhole(e.target.checked, selection))} />
              Whole dial
            </label>
          </td>
          <td style={roleCell}>
            {selection.whole && <RoleSelect value={selection.wholeRole} label="Role on the whole dial" onChange={(r) => onChange(setWholeRole(r, selection))} />}
          </td>
        </tr>
        {sections.map((section) => (
          <Fragment key={section.key}>
            <GrantRow
              label={section.name}
              depth={0}
              leaves={sectionLeaves(section)}
              selection={selection}
              role={role}
              off={off}
              onChange={onChange}
              cell={cell}
              roleCell={roleCell}
            />
            {section.segments.map((sg) => (
              <Fragment key={sg.key}>
                <GrantRow
                  label={sg.name}
                  context={section.name}
                  depth={1}
                  leaves={segmentLeaves(section.key, sg)}
                  selection={selection}
                  role={role}
                  off={off}
                  onChange={onChange}
                  cell={cell}
                  roleCell={roleCell}
                />
                {(sg.items ?? []).map((item) => (
                  <GrantRow
                    key={item.key}
                    label={item.name}
                    context={`${section.name} › ${sg.name}`}
                    depth={2}
                    leaves={[leafKey(section.key, sg.key, item.key)]}
                    selection={selection}
                    role={role}
                    off={off}
                    onChange={onChange}
                    cell={cell}
                    roleCell={roleCell}
                  />
                ))}
              </Fragment>
            ))}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

/** One row of the matrix: a section, an action point or an item, standing for the leaves beneath it. */
function GrantRow({
  label, context, depth, leaves, selection, role, off, onChange, cell, roleCell,
}: {
  label: string;
  /** Where the row sits, read to screen readers after its name ("in Health"). */
  context?: string;
  depth: 0 | 1 | 2;
  leaves: string[];
  selection: GuestSelection;
  role: string;
  off: boolean;
  onChange: (next: GuestSelection) => void;
  cell: React.CSSProperties;
  roleCell: React.CSSProperties;
}) {
  const boxRef = useRef<HTMLInputElement>(null);
  const ticked = leaves.filter((l) => selection.leaves.has(l)).length;
  const state = ticked === 0 ? "none" : ticked === leaves.length ? "all" : "some";
  useEffect(() => {
    if (boxRef.current) boxRef.current.indeterminate = state === "some";
  }, [state]);
  const current = rowRole(leaves, selection);
  return (
    <tr style={{ opacity: off ? 0.5 : 1 }}>
      <td style={{ ...cell, paddingLeft: 10 + depth * 24 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: off ? "default" : "pointer", fontWeight: depth === 0 ? 600 : 400 }}>
          <input
            ref={boxRef}
            type="checkbox"
            checked={state === "all"}
            disabled={off}
            onChange={() => onChange(toggleLeaves(leaves, selection, role))}
          />
          {label}
          {context && (
            <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
              , in {context}
            </span>
          )}
        </label>
      </td>
      <td style={roleCell}>
        {!off && current !== null && (
          <RoleSelect
            value={current}
            label={`Role on ${label}`}
            onChange={(r) => onChange(setRowRole(leaves, selection, r))}
          />
        )}
      </td>
    </tr>
  );
}

function RoleSelect({ value, label, onChange }: { value: string; label: string; onChange: (role: string) => void }) {
  return (
    <select
      value={value}
      aria-label={label}
      onChange={(e) => onChange(e.target.value)}
      style={{ fontSize: 12.5, padding: "3px 6px", borderRadius: 6, border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)" }}
    >
      {value === "mixed" && <option value="mixed" disabled>Mixed</option>}
      {GUEST_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
    </select>
  );
}
